import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { INTERRUPTED_ERROR } from '@crowdex/agent-contracts';

/**
 * Something cut off at the tail of a resumed conversation that the user can act
 * on: a tool call (often awaiting approval) or a sub-agent task. `kind`
 * discriminates so the continuation prompts read correctly.
 */
export type InterruptedToolAction = {
  kind: 'tool' | 'subagent';
  toolCallId: string;
  toolName: string;
  title?: string;
  input: unknown;
};

/**
 * Resolves the item the user can resume after reopening a conversation: an
 * interrupted `dynamic-tool` part or a cut-off sub-agent sitting at the very end
 * of the transcript. Returns `null` unless the last meaningful message is an
 * assistant turn whose trailing item was interrupted — a later user reply or a
 * completed turn means the conversation already moved on.
 */
export function interruptedToolAction(
  messages: readonly CrowdexUIMessage[],
): InterruptedToolAction | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message === undefined) continue;
    // Mode/model changes append a session-event-only assistant message; skip
    // those so a switch after the interruption does not hide the action.
    const hasMeaningfulPart = message.parts.some((part) => part.type !== 'data-sessionEvent');
    if (!hasMeaningfulPart) continue;
    if (message.role !== 'assistant') return null;
    for (let partIndex = message.parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = message.parts[partIndex];
      if (
        part?.type === 'dynamic-tool' &&
        part.state === 'output-error' &&
        part.errorText === INTERRUPTED_ERROR
      ) {
        return {
          kind: 'tool',
          toolCallId: part.toolCallId,
          toolName: part.toolName,
          ...(part.title === undefined ? {} : { title: part.title }),
          input: part.input ?? null,
        };
      }
      if (part?.type === 'data-subagent' && part.data.status === 'cancelled') {
        return {
          kind: 'subagent',
          toolCallId: part.data.toolCallId,
          toolName: part.data.title,
          title: part.data.title,
          input: { prompt: part.data.prompt, agentType: part.data.agentType },
        };
      }
    }
    return null;
  }
  return null;
}

/** Agent-facing continuation sent when the user re-approves an interrupted action. */
export function resumeActionPrompt(action: InterruptedToolAction): string {
  if (action.kind === 'subagent') {
    const prompt =
      typeof (action.input as { prompt?: unknown })?.prompt === 'string'
        ? (action.input as { prompt: string }).prompt
        : '';
    return [
      `<resume_interrupted_subagent title="${action.toolName}"/>`,
      `The session was interrupted while the "${action.toolName}" sub-agent task was running. First check whether it already completed or partially applied; only re-run the parts that did not take effect, then continue.`,
      ...(prompt.length === 0 ? [] : ['', 'Task:', prompt]),
    ].join('\n');
  }
  return [
    `<resume_interrupted_action tool="${action.toolName}"/>`,
    `The session was interrupted around this "${action.toolName}" tool call, so it may never have run OR may have already partially applied. I approve it — but first verify the current state; if it did not take effect, perform it now, otherwise skip it to avoid doing it twice. Then continue.`,
    '',
    'Arguments:',
    '```json',
    JSON.stringify(action.input ?? null, null, 2),
    '```',
  ].join('\n');
}

/** Agent-facing continuation sent when the user declines an interrupted action. */
export function skipActionPrompt(action: InterruptedToolAction): string {
  if (action.kind === 'subagent') {
    return [
      `<skip_interrupted_subagent title="${action.toolName}"/>`,
      `I don't want to re-run the "${action.toolName}" sub-agent task that was interrupted. Continue without it.`,
    ].join('\n');
  }
  return [
    `<skip_interrupted_action tool="${action.toolName}"/>`,
    `I declined the "${action.toolName}" action you proposed before the interruption. Continue without it.`,
  ].join('\n');
}
